import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Play, Pause, Settings } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useOrderContext } from '../../../contexts/OrderContext';
import { usePositionContext } from '../../../contexts/PositionContext';
import { useAccount } from '../../../contexts/AccountContext';
import { useAccountType } from '../../../contexts/AccountTypeContext';
import { useAITrading } from '../../../contexts/AITradingContext';

interface SuperTrendStrategyProps {
  symbol: string;
  currentPrice?: string;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  onConfigUpdate?: (config: any) => void;
  isRunning?: boolean;
  initialConfig?: SuperTrendConfig; // Add prop for initial configuration
}

interface SuperTrendConfig {
  isActive: boolean;
  tradeAmountPercent: number;
  quantity: number;
  profitThreshold: number;
  lossThreshold: number;
  // VidyaHeart Strategy 2 Configuration
  initialEntryLevel: string;
  vidyaLength: number;
  vidyaMomentum: number;
  bandDistance: number;
}

const SuperTrendStrategy: React.FC<SuperTrendStrategyProps> = ({ symbol, currentPrice, onSignalUpdate, onConfigUpdate, isRunning: externalIsRunning, initialConfig }) => {
  const { theme } = useTheme();
  const { setOrderDetails, setShowOrderConfirmation, showConfirmationPopup } = useOrderContext();
  const { addOpenPosition } = usePositionContext();
  const { getAccountBalance } = useAccount();
  const { accountType } = useAccountType();
  
  // Get account-specific balance data
  const accountBalance = getAccountBalance(accountType);
  const { executeAITrade, updateSignal } = useAITrading();
  
  const [config, setConfig] = useState<SuperTrendConfig>(() => {
    // Use initialConfig if provided, otherwise use sensible defaults
    if (initialConfig) {
      console.log(`🔄 SuperTrend: Initializing with saved config for ${symbol}:`, initialConfig);
      return {
        isActive: initialConfig.isActive ?? false,
        tradeAmountPercent: initialConfig.tradeAmountPercent ?? 100,
        quantity: initialConfig.quantity ?? 0.5, // Default to 0.5 if not set
        profitThreshold: initialConfig.profitThreshold ?? 2.9, // Default to $5 if not set
        lossThreshold: initialConfig.lossThreshold ?? 2.9, // Default to $3 if not set
        // VidyaHeart Strategy 2 defaults
        initialEntryLevel: initialConfig.initialEntryLevel ?? '1m',
        vidyaLength: initialConfig.vidyaLength ?? 14,
        vidyaMomentum: initialConfig.vidyaMomentum ?? 20,
        bandDistance: initialConfig.bandDistance ?? 3.0,
      };
    }
    
    // Sensible default configuration for new strategies
    return {
      isActive: false,
      tradeAmountPercent: 100, // Default 100% of balance
      quantity: 0.5,     // Default quantity
      profitThreshold: 2.9,  // Default profit threshold
      lossThreshold: 2.9,   // Default loss threshold
      // VidyaHeart Strategy 2 defaults
      initialEntryLevel: '1m',
      vidyaLength: 14,
      vidyaMomentum: 20,
      bandDistance: 3.0,
    };
  });
  
  const [currentSignal, setCurrentSignal] = useState<'buy' | 'sell' | null>(null);
  const [lastSignal, setLastSignal] = useState<'buy' | 'sell' | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false); // Track if user has made changes

  // Use external isRunning if provided, otherwise use internal state
  const actualIsRunning = externalIsRunning !== undefined ? externalIsRunning : isRunning;

  // 🔥 CRITICAL FIX: Update config when initialConfig changes, but only if user hasn't edited
  useEffect(() => {
    if (initialConfig && !hasUserEdited) {
      setConfig({
        isActive: initialConfig.isActive ?? false,
        tradeAmountPercent: initialConfig.tradeAmountPercent ?? 100,
        quantity: initialConfig.quantity ?? 0.5, // Default to 0.5 if not set
        profitThreshold: initialConfig.profitThreshold ?? 2.9, // Default to $5 if not set
        lossThreshold: initialConfig.lossThreshold ?? 2.9, // Default to $3 if not set
        // VidyaHeart Strategy 2 Configuration
        initialEntryLevel: initialConfig.initialEntryLevel ?? '1m',
        vidyaLength: initialConfig.vidyaLength ?? 14,
        vidyaMomentum: initialConfig.vidyaMomentum ?? 20,
        bandDistance: initialConfig.bandDistance ?? 3.0,
      });
    }
  }, [initialConfig?.quantity, initialConfig?.profitThreshold, initialConfig?.lossThreshold, initialConfig?.isActive, initialConfig?.tradeAmountPercent, initialConfig?.initialEntryLevel, initialConfig?.vidyaLength, initialConfig?.vidyaMomentum, initialConfig?.bandDistance, symbol, hasUserEdited]);

  // Component initialization
  
  // Handle isRunning prop changes

  // Notify parent component when config changes
  useEffect(() => {
    onConfigUpdate?.(config);
  }, [config, onConfigUpdate]);

  // State for SuperTrend calculation
  const [priceHistory, setPriceHistory] = useState<Array<{price: number, timestamp: number}>>([]);
  const [lastDirection, setLastDirection] = useState<number | null>(null);
  const [lastSignalTime, setLastSignalTime] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  // VidyaHeart Strategy 2 state variables
  const [usedLevels, setUsedLevels] = useState<{[key: string]: boolean}>({
    '1m': false,
    '5m': false,
    '15m': false,
    '1h': false,
    '1d': false,
    '1w': false,
    '1M': false
  });
  const [initialEntryLevel, setInitialEntryLevel] = useState<string>('');
  const [vidyaPriceHistory, setVidyaPriceHistory] = useState<Array<{price: number, timestamp: number}>>([]);
  const [smoothedLevels, setSmoothedLevels] = useState<{[key: string]: number}>({});

  // Calculate Smoothed Levels for VidyaHeart Strategy 2 (matches backend logic)
  const calculateSmoothedLevels = useCallback((prices: Array<{price: number, timestamp: number}>, vidyaLength: number, vidyaMomentum: number, bandDistance: number): {[key: string]: number} => {
    if (prices.length < vidyaLength + vidyaMomentum) {
      return {};
    }
    
    // Convert price data to closes array (for compatibility with backend)
    const closes = prices.map(p => p.price);
    
    // Calculate VIDYA (Variable Index Dynamic Average) - matches backend
    const vidya = calculateVIDYA(closes, vidyaLength, vidyaMomentum);
    if (!vidya) return {};
    
    // Calculate ATR for bands - matches backend
    const atr = calculateATR(closes, vidyaLength);
    if (!atr) return {};
    
    // Generate smoothed levels for different timeframes - matches backend
    const currentVidya = vidya[vidya.length - 1];
    const currentATR = atr[atr.length - 1];
    
    return {
      '1m': currentVidya - (currentATR * bandDistance * 0.1),
      '5m': currentVidya - (currentATR * bandDistance * 0.2),
      '15m': currentVidya - (currentATR * bandDistance * 0.3),
      '1h': currentVidya - (currentATR * bandDistance * 0.5),
      '1d': currentVidya - (currentATR * bandDistance * 0.7),
      '1w': currentVidya - (currentATR * bandDistance * 0.9),
      '1M': currentVidya - (currentATR * bandDistance * 1.0)
    };
  }, []);

  // Calculate VIDYA (Variable Index Dynamic Average) - matches backend
  const calculateVIDYA = useCallback((closes: number[], length: number, momentum: number): number[] | null => {
    if (closes.length < length + momentum) return null;

    const vidya: number[] = [];
    const alpha = 2 / (length + 1);

    // Calculate momentum
    const momentumValues: number[] = [];
    for (let i = momentum; i < closes.length; i++) {
      const momentumValue = Math.abs(closes[i] - closes[i - momentum]) / closes[i - momentum];
      momentumValues.push(momentumValue);
    }

    // Calculate VIDYA
    let vidyaValue = closes[length - 1]; // Start with SMA
    vidya.push(vidyaValue);

    for (let i = length; i < closes.length; i++) {
      const momentumIndex = i - length;
      const momentumValue = momentumValues[momentumIndex];
      const adaptiveAlpha = alpha * (1 + momentumValue);
      
      vidyaValue = adaptiveAlpha * closes[i] + (1 - adaptiveAlpha) * vidyaValue;
      vidya.push(vidyaValue);
    }

    return vidya;
  }, []);

  // Calculate ATR (Average True Range) - matches backend
  const calculateATR = useCallback((closes: number[], period: number): number[] | null => {
    if (closes.length < period + 1) return null;

    const trueRanges: number[] = [];
    
    for (let i = 1; i < closes.length; i++) {
      // For single price data, use price as both high and low
      const high = closes[i];
      const low = closes[i];
      const prevClose = closes[i - 1];
      
      const tr = Math.max(
        high - low,
        Math.abs(high - prevClose),
        Math.abs(low - prevClose)
      );
      trueRanges.push(tr);
    }
    
    if (trueRanges.length < period) return null;
    
    // Calculate ATR as simple moving average of true ranges
    const atr: number[] = [];
    for (let i = period - 1; i < trueRanges.length; i++) {
      const recentTR = trueRanges.slice(i - period + 1, i + 1);
      const avgTR = recentTR.reduce((sum, tr) => sum + tr, 0) / period;
      atr.push(avgTR);
    }
    
    return atr;
  }, []);

  // VidyaHeart Strategy 2 calculation with real market data
  const calculateVidyaHeartSignal = useCallback(() => {
    if (!currentPrice || !actualIsRunning || isProcessing) {
      return;
    }
    
    const price = parseFloat(currentPrice);
    const now = Date.now();
    
    // Check minimum timeframe (2 minutes = 120000ms) - VidyaHeart Strategy 2
    const minTimeframe = 120000; // 2 minutes - reduces computational load and overtrading
    if (now - lastSignalTime < minTimeframe) {
      return;
    }
    
    // Set processing flag to prevent concurrent executions
    setIsProcessing(true);
    
    // Add current price to VidyaHeart price history
    const newVidyaPriceHistory = [...vidyaPriceHistory, { price, timestamp: now }];
    
    // Keep only last 100 prices for VidyaHeart calculation
    const limitedVidyaHistory = newVidyaPriceHistory.slice(-100);
    setVidyaPriceHistory(limitedVidyaHistory);
    
    // Need at least 30 prices for reliable VidyaHeart calculation
    if (limitedVidyaHistory.length < 30) {
      // console.log(`⏳ VidyaHeart: Building price history (${limitedVidyaHistory.length}/30 prices needed)`);
      setIsProcessing(false);
      return;
    }
    
    // Calculate smoothed levels using VidyaHeart Strategy 2
    const smoothedLevels = calculateSmoothedLevels(
      limitedVidyaHistory, 
      config.vidyaLength, 
      config.vidyaMomentum, 
      config.bandDistance
    );
    
    if (!smoothedLevels) {
      setIsProcessing(false);
      return;
    }
    
    // Progressive level selection logic
    const levelOrder = ['1m', '5m', '15m', '1h', '1d', '1w', '1M'];
    const startIndex = levelOrder.indexOf(config.initialEntryLevel);
    
    if (startIndex === -1) {
      setIsProcessing(false);
      return; // Invalid initial entry level
    }
    
    // Find the first available level (below current price - green heart condition)
    let selectedLevel = null;
    let selectedValue = null;
    
    for (let i = startIndex; i < levelOrder.length; i++) {
      const level = levelOrder[i];
      const levelValue = smoothedLevels[level];
      
      if (levelValue && levelValue < price) {
        selectedLevel = level;
        selectedValue = levelValue;
        break;
      }
    }
    
    if (!selectedLevel || !selectedValue) {
      // console.log(`📊 VidyaHeart: No available levels below current price $${price}`);
      setIsProcessing(false);
      return; // No available levels
    }
    
    // Crossover detection: previous low > level AND current low <= level
    // Use previous price as "previous low" and current price as "current low"
    const previousLow = limitedVidyaHistory.length >= 2 ? limitedVidyaHistory[limitedVidyaHistory.length - 2].price : price;
    const currentLow = price; // Current price acts as current low
    const isCrossover = previousLow > selectedValue && currentLow <= selectedValue;
    
    if (isCrossover) {
      const signal = 'buy';
      setCurrentSignal(signal);
      setLastSignal(signal);
      setLastSignalTime(now);
      onSignalUpdate?.(signal);
      updateSignal(symbol, signal);
      
      // Execute AI trade when crossover detected
      console.log(`🎯 VidyaHeart executing ${signal.toUpperCase()} trade for ${symbol} at level ${selectedLevel} (${selectedValue})`);
      console.log(`📊 Crossover: Previous Low ${previousLow} > Level ${selectedValue} AND Current Low ${currentLow} <= Level ${selectedValue}`);
      executeAITrade(symbol, signal, currentPrice);
    } else {
      console.log(`📊 VidyaHeart: No crossover detected at ${selectedLevel} level (${selectedValue})`);
      console.log(`📊 Previous Low: ${previousLow}, Current Low: ${currentLow}, Level: ${selectedValue}`);
    }
    
    // Clear processing flag
    setIsProcessing(false);
  }, [
    currentPrice, 
    actualIsRunning, 
    isProcessing,
    vidyaPriceHistory, 
    lastSignalTime, 
    config.initialEntryLevel,
    config.vidyaLength,
    config.vidyaMomentum,
    config.bandDistance,
    onSignalUpdate, 
    symbol, 
    updateSignal,
    executeAITrade,
    calculateSmoothedLevels,
    calculateVIDYA,
    calculateATR
  ]);


  // Calculate VidyaHeart Strategy 2 periodically
  useEffect(() => {
    if (!actualIsRunning) {
      return;
    }
    
    // Check every 2 minutes for price updates - TradingView Standard frequency
    const intervalTime = 120000; // 2 minutes - reduces computational load and overtrading
    const interval = setInterval(calculateVidyaHeartSignal, intervalTime);
    return () => {
      clearInterval(interval);
    };
  }, [calculateVidyaHeartSignal, actualIsRunning, symbol]);

  const handleStartStop = () => {
    setIsRunning(!isRunning);
    if (!isRunning) {
      // Reset all state when starting
      setCurrentSignal(null);
      setLastSignal(null);
      setPriceHistory([]);
      setLastDirection(null);
      setLastSignalTime(0);
      setIsProcessing(false);
      
      // Reset VidyaHeart Strategy 2 state
      setVidyaPriceHistory([]);
      setSmoothedLevels({});
      setInitialEntryLevel('');
      setUsedLevels({
        '1m': false,
        '5m': false,
        '15m': false,
        '1h': false,
        '1d': false,
        '1w': false,
        '1M': false
      });
      
      // Log startup message
      // console.log(`🚀 SuperTrend Strategy Started for ${symbol}`);
      // console.log(`⏳ Building price history - will start trading after collecting 20 price points`);
      // console.lo g(`📊 Parameters: ATR Period=${config.period}, Multiplier=${config.multiplier}`);
    } else {
      // console.log(`⏸️ SuperTrend Strategy Stopped for ${symbol}`);
    }
  };

  const handleConfigChange = (field: keyof SuperTrendConfig, value: number | boolean | string) => {
    setHasUserEdited(true); // Mark that user has made changes
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Strategy Status */}
      {actualIsRunning && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {vidyaPriceHistory.length < 30 ? (
                `Building price history (${vidyaPriceHistory.length}/30)`
              ) : (
                `Ready to trade - Monitoring ${symbol}`
              )}
            </span>
          </div>
          {vidyaPriceHistory.length >= 30 && (
            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
              Entry Level: {config.initialEntryLevel} | 
              Last Signal: {lastSignal || 'None'} | 
              Position: {currentSignal ? `${currentSignal.toUpperCase()} Active` : 'None'}
              <br />
              <span className="text-green-600 dark:text-green-400">
                VIDYA Length: {config.vidyaLength} | Momentum: {config.vidyaMomentum} | Band Distance: {config.bandDistance}
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Strategy Configuration */}
      <div className="space-y-3">
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Trade Amount:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <span className="text-xs theme-text-quaternary">%</span>
            <input
              type="number"
              value={config.tradeAmountPercent}
              onChange={(e) => handleConfigChange('tradeAmountPercent', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="100"
              step="1"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Quantity:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <input
              type="number"
              value={config.quantity}
              onChange={(e) => handleConfigChange('quantity', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.01"
              max="1000"
              step="0.01"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Profit Threshold:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <span className="text-xs theme-text-quaternary">$</span>
            <input
              type="number"
              value={config.profitThreshold}
              onChange={(e) => handleConfigChange('profitThreshold', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.01"
              max="100"
              step="0.01"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Loss Threshold:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <span className="text-xs theme-text-quaternary">$</span>
            <input
              type="number"
              value={config.lossThreshold}
              onChange={(e) => handleConfigChange('lossThreshold', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.01"
              max="100"
              step="0.01"
            />
          </div>
        </div>
        
        {/* VidyaHeart Strategy 2 Configuration */}
        <div className="border-t theme-border pt-3 mt-3">
          <div className="text-xs font-medium theme-text-secondary mb-2">VidyaHeart Strategy 2</div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">Initial Entry Level:</span>
            <select
              value={config.initialEntryLevel}
              onChange={(e) => handleConfigChange('initialEntryLevel', e.target.value)}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
            >
              <option value="1m">1m</option>
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="1h">1h</option>
              <option value="1d">1d</option>
              <option value="1w">1w</option>
              <option value="1M">1M</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">VIDYA Length:</span>
            <input
              type="number"
              value={config.vidyaLength}
              onChange={(e) => handleConfigChange('vidyaLength', parseInt(e.target.value))}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="50"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">VIDYA Momentum:</span>
            <input
              type="number"
              value={config.vidyaMomentum}
              onChange={(e) => handleConfigChange('vidyaMomentum', parseInt(e.target.value))}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="50"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">Band Distance:</span>
            <input
              type="number"
              value={config.bandDistance}
              onChange={(e) => handleConfigChange('bandDistance', parseFloat(e.target.value))}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.1"
              max="10"
              step="0.1"
            />
          </div>
        </div>
        
        {/* Trade Amount Display */}
        {accountBalance && (
          <div className="flex items-center justify-between pt-2 border-t theme-border">
            <span className="text-xs theme-text-quaternary">Trade Value:</span>
            <span className="text-xs font-medium theme-text-primary">
              ${(accountBalance.totalBalance * config.tradeAmountPercent / 100).toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperTrendStrategy;
/* last changes */